import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	ReactFlow,
	Background,
	Controls,
	addEdge,
	useNodesState,
	useEdgesState,
	MiniMap,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
	ArrowLeft,
	Save,
	Play,
	History,
	Trash2,
	Copy,
	Loader2,
	LayoutGrid,
	// Apelidado: importado como "Map", o ícone sobrescreve o Map do JavaScript
	// e todo `new Map()` deste arquivo estoura com "Map is not a constructor".
	Map as MapaIcone,
	RefreshCw,
  Upload,
  Plus,
  Mic,
  Video,
  Trash,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "../../components/ui/sheet";

import useMoedaAtiva, {
	moedaPlataforma,
	simboloMoeda,
} from "../../hooks/useMoedaAtiva";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { getBackendUrl } from "../../config";
// A paleta, os rótulos, os padrões e o mapa de componentes vêm todos do
// catálogo de nós — uma entrada por nó, em vez de quatro listas paralelas.
import { nodeTypes, NODE_DEFAULTS, NODE_LABELS, PALETTE } from "./catalog";
import { ACENTOS } from "./nodes";
import { cn } from "../../lib/utils";
import { useLocalStorage } from "../../hooks/useLocalStorage";



const statusVariant = {
	pending: "secondary",
	running: "default",
	waiting: "secondary",
	completed: "secondary",
	failed: "destructive",
};

const statusLabel = {
	pending: "Pendente",
	running: "Executando",
	waiting: "Aguardando resposta",
	completed: "Concluída",
	failed: "Falhou",
};

const FlowBuilder = () => {
	useMoedaAtiva();
	const navigate = useNavigate();
	const { flowId } = useParams();
	const { whatsApps } = useWhatsApps();

	const [flow, setFlow] = useState(null);
	const [name, setName] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	// Quando gravou pela última vez — é o que a barra mostra no lugar do aviso
	// que o salvamento automático deixou de dar.
	const [salvoEm, setSalvoEm] = useState(null);
	// Gaveta de blocos do celular.
	const [paletaAberta, setPaletaAberta] = useState(false);

	// Serialização do último conteúdo gravado, para saber se algo mudou de
	// verdade. Nulo enquanto o fluxo não foi lido.
	const ultimoSalvo = useRef(null);

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [selectedNodeId, setSelectedNodeId] = useState(null);
	const [enviandoArquivo, setEnviandoArquivo] = useState(false);
	const [novaPalavra, setNovaPalavra] = useState("");
	const arquivoRef = useRef(null);
	const arquivoItemRef = useRef(null);
	const itemDoUpload = useRef(0);

	const [testDialogOpen, setTestDialogOpen] = useState(false);
	const [testInput, setTestInput] = useState('{\n  "name": "Cliente"\n}');
	const [executionsOpen, setExecutionsOpen] = useState(false);
	const [executions, setExecutions] = useState([]);
	const [loadingExecutions, setLoadingExecutions] = useState(false);

	// Filas, etiquetas e atendentes alimentam os nós que agem sobre o
	// atendimento.
	const [filas, setFilas] = useState([]);
	const [etiquetas, setEtiquetas] = useState([]);
	const [atendentes, setAtendentes] = useState([]);
	const [raias, setRaias] = useState([]);

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get(`/flows/${flowId}`);
				// Fluxo novo, referência nova: senão o payload do anterior
				// contaria como "já salvo" e a primeira edição aqui passaria batida.
				ultimoSalvo.current = null;
				setFlow(data);
				setName(data.name);
				setNodes(
					(data.nodes || []).map((node) => ({
						...node,
						deletable: node.type !== "trigger",
					}))
				);
				setEdges(data.edges || []);
			} catch (err) {
				toastError(err);
			}
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flowId]);

	useEffect(() => {
		(async () => {
			try {
				const [q, t, u, r] = await Promise.all([
					api.get("/queue"),
					api.get("/tags"),
					api.get("/users"),
					api.get("/ticket-lanes"),
				]);
				setFilas(q.data || []);
				setEtiquetas(t.data?.tags || t.data || []);
				setAtendentes(u.data?.users || u.data || []);
				setRaias(r.data?.lanes || r.data || []);
			} catch (err) {
				// Sem as listas os nós ainda abrem; só o seletor fica vazio.
			}
		})();
	}, []);

	const selectedNode = useMemo(
		() => nodes.find((n) => n.id === selectedNodeId) || null,
		[nodes, selectedNodeId]
	);

	// Instância do canvas (para centralizar na hora de criar) e a caixa dele
	// (para saber o tamanho da área visível).
	const fluxoRef = useRef(null);
	const canvasRef = useRef(null);

	// Bloco recém-criado (piscando) e o minimapa, que fica na escolha de cada
	// um — em fluxo pequeno ele só ocupa espaço.
	const [noNovo, setNoNovo] = useState(null);
	const [minimapaAberto, setMinimapaAberto] = useLocalStorage(
		"flowMinimapa",
		true,
	);

	const onConnect = useCallback(
		(params) => setEdges((eds) => addEdge(params, eds)),
		[setEdges]
	);

	// Blocos que abrem vários caminhos (cada saída é um destino) e os que
	// encerram o fluxo. Nos dois casos, ligar sozinho seria chute: no primeiro
	// não se sabe por qual saída seguir, no segundo não há saída nenhuma.
	const MULTIPLAS_SAIDAS = [
		"condition",
		"menu",
		"buttons",
		"list",
		"randomizer",
		"trigger",
	];
	const SEM_SAIDA = ["end", "jump"];

	const SAIDA_UNICA = (tipo) =>
		!MULTIPLAS_SAIDAS.includes(tipo) && !SEM_SAIDA.includes(tipo);

	// Largura/altura aproximadas do cartão de nó. Servem para afastar um do
	// outro; não precisam ser exatas, só coerentes com o desenho.
	const LARGURA_NO = 300;
	const ALTURA_NO = 150;

	// Livre = nenhum nó existente encostando. Sem isto, o nó novo nascia POR
	// CIMA de outro e sumia — a pessoa clicava de novo achando que não tinha
	// funcionado, e acabava com três cópias empilhadas.
	const posicaoLivre = (inicial, existentes) => {
		const colide = (p) =>
			existentes.some(
				(n) =>
					Math.abs(n.position.x - p.x) < LARGURA_NO &&
					Math.abs(n.position.y - p.y) < ALTURA_NO
			);

		const alvo = { ...inicial };
		let tentativas = 0;

		// Desce em degraus até achar espaço; passando de 40 tentativas, algo está
		// muito errado e vale mais soltar o nó do que travar a tela.
		while (colide(alvo) && tentativas < 40) {
			alvo.y += ALTURA_NO + 30;
			tentativas += 1;
		}

		return alvo;
	};

	// Arruma o fluxo inteiro seguindo as ligações.
	//
	// É a saída para quem já se perdeu: com trinta blocos espalhados pelo
	// canvas, achar "onde a conversa continua" é impossível no olho. Aqui os
	// blocos viram camadas — quem começa em cima, o que vem depois logo abaixo,
	// e os caminhos paralelos lado a lado.
	const organizarFluxo = () => {
		if (!nodes.length) return;

		const saidasDe = new Map();
		const temEntrada = new Set();

		edges.forEach((e) => {
			saidasDe.set(e.source, [...(saidasDe.get(e.source) || []), e.target]);
			temEntrada.add(e.target);
		});

		// Começo: os gatilhos e, na falta deles, qualquer bloco que ninguém
		// aponta. Fluxo circular sem começo nenhum cai no primeiro da lista.
		const raizes = nodes.filter(
			(n) => n.type === "trigger" || !temEntrada.has(n.id)
		);
		const fila = (raizes.length ? raizes : [nodes[0]]).map((n) => n.id);

		const camadaDe = new Map();
		fila.forEach((id) => camadaDe.set(id, 0));

		// Largura primeiro: cada bloco fica na camada do caminho mais curto até
		// ele, que é como a pessoa lê o fluxo.
		for (let i = 0; i < fila.length; i += 1) {
			const atual = fila[i];
			const proxima = (camadaDe.get(atual) || 0) + 1;

			(saidasDe.get(atual) || []).forEach((destino) => {
				if (camadaDe.has(destino)) return;
				camadaDe.set(destino, proxima);
				fila.push(destino);
			});
		}

		// Quem ficou de fora (bloco solto, sem ligação nenhuma) vai para uma
		// camada final própria, em vez de sumir por cima de outro.
		const maiorCamada = Math.max(0, ...camadaDe.values());
		nodes.forEach((n) => {
			if (!camadaDe.has(n.id)) camadaDe.set(n.id, maiorCamada + 1);
		});

		const porCamada = new Map();
		nodes.forEach((n) => {
			const c = camadaDe.get(n.id) || 0;
			porCamada.set(c, [...(porCamada.get(c) || []), n.id]);
		});

		const ESPACO_X = LARGURA_NO + 60;
		const ESPACO_Y = ALTURA_NO + 90;

		const posicoes = new Map();

		[...porCamada.entries()]
			.sort((a, b) => a[0] - b[0])
			.forEach(([camada, ids]) => {
				const larguraTotal = (ids.length - 1) * ESPACO_X;

				ids.forEach((id, indice) => {
					posicoes.set(id, {
						x: 120 + indice * ESPACO_X - larguraTotal / 2,
						y: 80 + camada * ESPACO_Y,
					});
				});
			});

		setNodes((nds) =>
			nds.map((n) => ({ ...n, position: posicoes.get(n.id) || n.position }))
		);

		// Depois de arrumar, mostra o fluxo inteiro: é a foto que faz a pessoa
		// reencontrar o que estava procurando.
		requestAnimationFrame(() =>
			fluxoRef.current?.fitView({ padding: 0.2, duration: 500 })
		);

		toast.success("Fluxo organizado.");
	};

	const handleAddNode = (type) => {
		const id = `${type}-${Date.now()}`;

		// Onde o nó nasce é o que fazia a pessoa se perder: ele ia 200px abaixo
		// do ÚLTIMO da lista — que é o mais recente, não o que está na tela.
		// Em fluxo grande isso jogava o nó para fora da área visível e parecia
		// que o clique não tinha feito nada.
		//
		// Agora nasce onde a pessoa está olhando: ao lado do nó selecionado, ou
		// no centro do que está à vista quando não há seleção.
		const referencia = nodes.find((n) => n.id === selectedNodeId);

		let inicial;

		if (referencia) {
			inicial = {
				x: referencia.position.x,
				y: referencia.position.y + ALTURA_NO + 60,
			};
		} else if (fluxoRef.current) {
			const { x, y, zoom } = fluxoRef.current.getViewport();
			const area = canvasRef.current?.getBoundingClientRect();
			const largura = area?.width || window.innerWidth;
			const altura = area?.height || window.innerHeight;

			// Centro da tela convertido para a coordenada do canvas.
			inicial = {
				x: (largura / 2 - x) / zoom - LARGURA_NO / 2,
				y: (altura / 2 - y) / zoom - ALTURA_NO / 2,
			};
		} else {
			inicial = { x: 360, y: 100 };
		}

		const position = posicaoLivre(inicial, nodes);

		// Nasce solto, sem ligação. Ligar sozinho no último nó parecia ajudar,
		// mas o "último" é só o mais recente da lista — quase nunca o lugar onde
		// a pessoa queria continuar. Quem monta o fluxo puxa a linha de onde quer.
		setNodes((nds) => [
			...nds,
			{ id, type, position, data: { ...NODE_DEFAULTS[type] }, deletable: true },
		]);

		// Já liga no bloco de onde partiu — mas só quando aquele bloco tem UMA
		// saída e ela está livre. Bloco de vários caminhos (condição, menu) fica
		// sem ligação de propósito: adivinhar por qual saída seguir daria a
		// ligação errada, que é pior do que ligação nenhuma.
		if (referencia && SAIDA_UNICA(referencia.type)) {
			const jaSai = edges.some((e) => e.source === referencia.id);

			if (!jaSai) {
				setEdges((eds) =>
					addEdge(
						{ source: referencia.id, target: id, sourceHandle: null },
						eds
					)
				);
			}
		}

		setSelectedNodeId(id);

		// Um piscar no bloco recém-criado: com o canvas cheio, ver ONDE ele
		// entrou é o que evita clicar de novo achando que nada aconteceu.
		setNoNovo(id);
		setTimeout(() => setNoNovo((atual) => (atual === id ? null : atual)), 1600);

		// E a tela vai até ele: ver o nó aparecer é o que fecha a dúvida de
		// "cliquei e não aconteceu nada".
		requestAnimationFrame(() => {
			fluxoRef.current?.setCenter(
				position.x + LARGURA_NO / 2,
				position.y + ALTURA_NO / 2,
				{ zoom: fluxoRef.current.getZoom(), duration: 400 }
			);
		});
	};

	// Sobe o arquivo para a biblioteca de mídias e guarda o nome dele no nó.
	// Guardamos o nome, não uma URL: o envio lê direto do disco, o que evita
	// fazer o servidor buscar um endereço dele mesmo — exatamente o que o
	// guarda de SSRF barra.
	const enviarArquivo = async (arquivo, aoConcluir) => {
		if (!arquivo) return;

		setEnviandoArquivo(true);

		try {
			const corpo = new FormData();
			corpo.append("medias", arquivo);
			corpo.append("name", arquivo.name);

			const { data } = await api.post("/media-library", corpo, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			aoConcluir(data.mediaUrl, data.mediaType);
			toast.success("Arquivo enviado.");
		} catch (err) {
			toastError(err);
		}

		setEnviandoArquivo(false);
	};

	const updateSelectedNodeData = (patch) => {
		setNodes((nds) =>
			nds.map((node) =>
				node.id === selectedNodeId
					? { ...node, data: { ...node.data, ...patch } }
					: node
			)
		);
	};

	// Palavras-chave do gatilho. Ficam num array no dado do nó; o campo de
	// digitação é local porque a palavra só entra na lista quando confirmada.
	const palavrasDoNo = useMemo(
		() =>
			Array.isArray(selectedNode?.data?.keywords)
				? selectedNode.data.keywords
				: [],
		[selectedNode]
	);

	const adicionarPalavra = () => {
		const palavra = novaPalavra.trim();

		if (!palavra) return;

		// Repetida não muda nada no resultado e só polui a lista.
		if (
			palavrasDoNo.some((p) => p.toLowerCase() === palavra.toLowerCase())
		) {
			setNovaPalavra("");
			return;
		}

		updateSelectedNodeData({ keywords: [...palavrasDoNo, palavra] });
		setNovaPalavra("");
	};

	const removerPalavra = (palavra) => {
		updateSelectedNodeData({
			keywords: palavrasDoNo.filter((p) => p !== palavra),
		});
	};

	const handleRemoveSelectedNode = () => {
		if (!selectedNode || selectedNode.type === "trigger") return;
		setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
		setEdges((eds) =>
			eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId)
		);
		setSelectedNodeId(null);
	};

	// O que de fato vai para o servidor. Só isto conta como "o fluxo mudou":
	// o React Flow pendura no nó coisas dele (medidas, seleção, se está sendo
	// arrastado) que não são conteúdo e não podem contar como edição.
	const montarPayload = useCallback(
		() => ({
			name,
			nodes: nodes.map(({ id, type, position, data }) => ({
				id,
				type,
				position,
				data,
			})),
			edges: edges.map(({ id, source, target, sourceHandle }) => ({
				id,
				source,
				target,
				sourceHandle: sourceHandle || null,
			})),
		}),
		[nodes, edges, name]
	);

	// Um gravador só, usado pelo botão e pelo salvamento automático.
	//
	// `silencioso` separa os dois: o clique no botão merece confirmação, o
	// automático não — um "salvo com sucesso" a cada nó arrastado viraria uma
	// fila de avisos cobrindo o próprio fluxo.
	const salvar = useCallback(
		async ({ silencioso = false } = {}) => {
			const payload = montarPayload();
			setSaving(true);
			try {
				const { data } = await api.put(`/flows/${flowId}`, payload);
				setFlow(data);
				ultimoSalvo.current = JSON.stringify(payload);
				setSalvoEm(new Date());
				if (!silencioso) toast.success("Fluxo salvo com sucesso.");
			} catch (err) {
				// Falha no automático também precisa aparecer: seguir editando
				// achando que está salvo é o pior desfecho possível.
				toastError(err);
			}
			setSaving(false);
		},
		[montarPayload, flowId]
	);

	const handleSave = () => salvar();

	// Salvamento automático.
	//
	// Grava quando o conteúdo muda de verdade, e só depois que a edição para —
	// sem o intervalo, arrastar um nó dispararia uma requisição por quadro de
	// animação.
	//
	// A comparação com o último payload salvo é o que segura o resto: logo
	// depois de montar, o React Flow mede os nós e reescreve o array, e uma
	// guarda de "primeiro desenho" deixava isso passar — abrir um fluxo o
	// regravava no servidor, mexendo no "Atualizado em" sem ninguém ter
	// editado nada (e podendo sobrescrever a versão de outra pessoa).
	useEffect(() => {
		if (loading || !flowId) return;

		const atual = JSON.stringify(montarPayload());

		// Primeira passada depois da carga: guarda o estado lido como
		// referência, sem gravar.
		if (ultimoSalvo.current === null) {
			ultimoSalvo.current = atual;
			return;
		}

		if (atual === ultimoSalvo.current) return;

		const relogio = setTimeout(() => salvar({ silencioso: true }), 1500);

		return () => clearTimeout(relogio);
	}, [montarPayload, loading, flowId, salvar]);

	const fetchExecutions = async () => {
		setLoadingExecutions(true);
		try {
			const { data } = await api.get(`/flows/${flowId}/executions`);
			setExecutions(data);
		} catch (err) {
			toastError(err);
		}
		setLoadingExecutions(false);
	};

	const handleOpenExecutions = () => {
		setExecutionsOpen(true);
		fetchExecutions();
	};

	const handleRunTest = async () => {
		let parsed = {};
		if (testInput.trim()) {
			try {
				parsed = JSON.parse(testInput);
			} catch (err) {
				toast.error("O payload de teste não é um JSON válido.");
				return;
			}
		}
		try {
			await api.post(`/flows/${flowId}/execute`, { input: parsed });
			toast.success("Execução iniciada.");
			setTestDialogOpen(false);
			handleOpenExecutions();
		} catch (err) {
			toastError(err);
		}
	};

	const webhookUrl =
		flow?.webhookToken && `${getBackendUrl()}/flow-webhooks/${flow.webhookToken}`;

	const handleCopyWebhook = () => {
		navigator.clipboard.writeText(webhookUrl);
		toast.success("URL do webhook copiada.");
	};

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Barra superior */}
			<div className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate("/flows")}
				>
					<ArrowLeft className="h-5 w-5" />
				</Button>
				{/* No celular a paleta não cabe ao lado do canvas; entra por aqui. */}
				<Button
					variant="outline"
					size="sm"
					className="shrink-0 sm:hidden"
					onClick={() => setPaletaAberta(true)}
				>
					<Plus className="h-4 w-4" />
					Bloco
				</Button>
				<Input
					value={name}
					onChange={(e) => setName(e.target.value)}
					className="h-9 max-w-xs font-medium"
				/>
				{flow?.nodes?.some?.(
					(n) => n.type === "trigger" && n.data?.triggerType === "webhook"
				) && (
					<button
						type="button"
						onClick={handleCopyWebhook}
						className="hidden items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs text-muted-foreground hover:bg-muted lg:flex"
						title={webhookUrl}
					>
						<Copy className="h-3 w-3" />
						Copiar URL do webhook
					</button>
				)}
				<div className="ml-auto flex items-center gap-2">
					{/* Arruma os blocos seguindo as ligações. É o botão de quem
					    abriu um fluxo antigo e não entende mais o desenho. */}
					<Button
						variant="outline"
						size="sm"
						onClick={organizarFluxo}
						title="Reorganizar os blocos seguindo as ligações"
					>
						<LayoutGrid className="h-4 w-4" />
						<span className="hidden sm:inline">Organizar</span>
					</Button>
					<Button variant="outline" size="sm" onClick={handleOpenExecutions}>
						<History className="h-4 w-4" />
						Execuções
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setTestDialogOpen(true)}
					>
						<Play className="h-4 w-4" />
						Executar teste
					</Button>
					{/* Estado do salvamento automático. Sem isto, quem edita não
					    tem como saber se o trabalho já foi gravado — e o botão
					    "Salvar" parado sugere que não foi. */}
					<span className="hidden text-xs text-muted-foreground sm:inline">
						{saving
							? "Salvando..."
							: salvoEm
								? `Salvo ${format(salvoEm, "HH:mm")}`
								: ""}
					</span>
					<Button size="sm" onClick={handleSave} disabled={saving}>
						{saving ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Save className="h-4 w-4" />
						)}
						Salvar
					</Button>
				</div>
			</div>

			{/* Blocos, no celular.
			    Dois por linha, com a largura inteira da tela: cada card tem
			    espaço para o nome completo, e a lista fica com metade da
			    altura que teria numa coluna só. Escolher um bloco já fecha a
			    gaveta e devolve o canvas. */}
			<Sheet open={paletaAberta} onOpenChange={setPaletaAberta}>
				<SheetContent
					side="bottom"
					className="h-[75dvh] rounded-t-2xl p-0 sm:hidden"
				>
					<div className="flex h-full flex-col">
						<div className="shrink-0 px-4 pb-2 pt-4">
							<h2 className="text-base font-semibold">Adicionar bloco</h2>
							<p className="text-xs text-muted-foreground">
								Ele entra no meio da tela; arraste para onde quiser.
							</p>
						</div>

						<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
							{PALETTE.map((secao) => (
								<div key={secao.grupo}>
									<p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
										{secao.grupo}
									</p>
									<div className="grid grid-cols-2 gap-2">
										{secao.itens.map((item) => (
											<button
												key={item.type}
												type="button"
												onClick={() => {
													handleAddNode(item.type);
													setPaletaAberta(false);
												}}
												className="flex min-h-14 items-center gap-2.5 rounded-xl border bg-card px-3 py-2.5 text-left text-[13px] font-medium transition-colors active:bg-accent"
											>
												<span
													className={cn(
														"flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
														ACENTOS[secao.acento]
													)}
												>
													<item.icon className="h-4 w-4" />
												</span>
												<span className="min-w-0 leading-tight">
													{item.label}
												</span>
											</button>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				</SheetContent>
			</Sheet>

			<div className="flex min-h-0 flex-1">
				{/* Paleta */}
				{/* Paleta.
				    No desktop é a coluna da esquerda. No celular ela some daqui:
				    espremida ao lado do canvas, sobrava largura para uma letra
				    do rótulo ("C...", "E..."), e os 20 blocos viravam uma
				    rolagem sem fim ao lado de um canvas de 200px. Lá ela é a
				    gaveta que o botão "Bloco" abre, com a largura toda. */}
				<div className="scrollbar-discreta hidden w-56 shrink-0 overflow-y-auto border-r bg-muted/30 p-3 sm:block">
					<p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
						Blocos
					</p>
					{PALETTE.map((secao) => (
						<div key={secao.grupo} className="pb-3">
							<p className="px-1 pb-1.5 text-[11px] font-medium text-muted-foreground">
								{secao.grupo}
							</p>
							<div className="space-y-1">
								{secao.itens.map((item) => (
									<button
										key={item.type}
										type="button"
										onClick={() => handleAddNode(item.type)}
										className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-transparent bg-card px-2 py-1.5 text-left text-[13px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:border-border hover:bg-accent"
									>
										{/* O chip repete a cor que o bloco terá no canvas:
										    a pessoa reconhece antes de ler o nome. */}
										<span
											className={cn(
												"flex h-5 w-5 shrink-0 items-center justify-center rounded-md",
												ACENTOS[secao.acento]
											)}
										>
											<item.icon className="h-3 w-3" />
										</span>
										<span className="truncate">{item.label}</span>
									</button>
								))}
							</div>
						</div>
					))}
				</div>

				{/* Canvas */}
				<div ref={canvasRef} className="flow-typebot min-w-0 flex-1 bg-muted/40">
					<ReactFlow
						onInit={(instancia) => {
							fluxoRef.current = instancia;
						}}
						nodes={nodes.map((n) =>
							n.id === noNovo ? { ...n, className: "flow-no-novo" } : n
						)}
						edges={edges}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onConnect={onConnect}
						onNodeClick={(_, node) => setSelectedNodeId(node.id)}
						onPaneClick={() => setSelectedNodeId(null)}
						nodeTypes={nodeTypes}
						fitView
						proOptions={{ hideAttribution: true }}
						// Curva suave em vez do traço reto: é o que faz o desenho
						// parecer um fluxo, e não um diagrama de rede.
						defaultEdgeOptions={{ type: "smoothstep", pathOptions: { borderRadius: 20 } }}
						connectionLineType="smoothstep"
					>
						<Background variant="dots" gap={22} size={1.4} className="opacity-60" />
						<Controls showInteractive={false} position="bottom-left" />

						{/* Minimapa: a bússola do fluxo grande. Fica na escolha de
						    cada um porque em fluxo de cinco blocos ele só ocupa o
						    canto. */}
						{minimapaAberto && (
							<MiniMap
								pannable
								zoomable
								position="bottom-right"
								className="!bottom-14 rounded-lg border shadow-sm"
								maskColor="hsl(var(--muted) / 0.6)"
								nodeColor={(n) =>
									n.id === selectedNodeId
										? "hsl(var(--primary))"
										: "hsl(var(--muted-foreground) / 0.5)"
								}
							/>
						)}

						<button
							type="button"
							onClick={() => setMinimapaAberto((v) => !v)}
							title={minimapaAberto ? "Esconder minimapa" : "Mostrar minimapa"}
							aria-label={
								minimapaAberto ? "Esconder minimapa" : "Mostrar minimapa"
							}
							className="absolute bottom-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg border bg-background/90 text-muted-foreground shadow-sm hover:text-foreground"
						>
							<MapaIcone className="h-4 w-4" />
						</button>
						{/* Sem minimapa: ninguém o usava para navegar, e ele cobria o
						    canto inferior direito da tela — justamente onde os nós
						    de fim de fluxo costumam ficar. */}
					</ReactFlow>
				</div>

				{/* Painel de configuração */}
				{selectedNode && (
					<div className="w-80 shrink-0 space-y-4 overflow-y-auto border-l bg-background p-4">
						<div className="flex items-center justify-between">
							<p className="text-sm font-semibold">
								{NODE_LABELS[selectedNode.type] || selectedNode.type}
							</p>
							{selectedNode.type !== "trigger" && (
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-destructive hover:text-destructive"
									onClick={handleRemoveSelectedNode}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							)}
						</div>

						{selectedNode.type === "trigger" && (
							<div className="space-y-1.5">
								<Label>Tipo de gatilho</Label>
								<Select
									value={selectedNode.data.triggerType || "manual"}
									onValueChange={(v) => updateSelectedNodeData({ triggerType: v })}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="manual">Manual</SelectItem>
										<SelectItem value="webhook">Webhook</SelectItem>
										<SelectItem value="keyword">Palavra-chave</SelectItem>
									</SelectContent>
								</Select>
								{selectedNode.data.triggerType === "keyword" ? (
									<p className="text-xs text-muted-foreground">
										O fluxo só começa quando a mensagem do cliente casar com uma
										das palavras. Sem palavra nenhuma, ele não responde.
									</p>
								) : (
									<p className="text-xs text-muted-foreground">
										Ligado a uma conexão, o fluxo roda para toda mensagem que
										chegar nela.
									</p>
								)}
							</div>
						)}

						{selectedNode.type === "typing" && (
							<div className="space-y-1.5">
								<Label htmlFor="typingSeconds">Por quantos segundos</Label>
								<Input
									id="typingSeconds"
									type="number"
									min={1}
									max={300}
									value={selectedNode.data.seconds ?? 3}
									onChange={(e) =>
										updateSelectedNodeData({ seconds: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">
									O fluxo espera esse tempo antes de seguir — sem a espera o
									aviso e a mensagem chegam juntos e ninguém chega a ver.
								</p>
							</div>
						)}

						{(selectedNode.type === "archive" ||
							selectedNode.type === "block") && (
							<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
								<div className="space-y-0.5">
									<Label htmlFor="desfazer">
										{selectedNode.type === "archive"
											? "Desarquivar em vez de arquivar"
											: "Desbloquear em vez de bloquear"}
									</Label>
									<p className="text-xs text-muted-foreground">
										{selectedNode.type === "block"
											? "Bloquear corta a conversa dos dois lados: o cliente não consegue mais escrever."
											: "Arquivar tira a conversa da lista sem encerrar o atendimento."}
									</p>
								</div>
								<Switch
									id="desfazer"
									checked={Boolean(selectedNode.data.desfazer)}
									onCheckedChange={(v) =>
										updateSelectedNodeData({ desfazer: v })
									}
								/>
							</div>
						)}

						{selectedNode.type === "board" && (
							<div className="space-y-1.5">
								<Label>Para qual raia</Label>
								<Select
									value={
										selectedNode.data.laneId
											? String(selectedNode.data.laneId)
											: undefined
									}
									onValueChange={(v) => {
										const raia = raias.find((r) => String(r.id) === v);
										updateSelectedNodeData({
											laneId: v,
											laneName: raia?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione a raia" />
									</SelectTrigger>
									<SelectContent>
										{raias.map((raia) => (
											<SelectItem key={raia.id} value={String(raia.id)}>
												{raia.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Move o atendimento no quadro de acompanhamento da equipe.
								</p>
							</div>
						)}

						{selectedNode.type === "trigger" && (
							<>
								<div className="space-y-1.5">
									<Label>Onde o fluxo vale</Label>
									<Select
										value={selectedNode.data.escopo || "individual"}
										onValueChange={(v) => updateSelectedNodeData({ escopo: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="individual">
												Conversa individual
											</SelectItem>
											<SelectItem value="grupo">Somente em grupos</SelectItem>
											<SelectItem value="ambos">
												Conversas individuais e grupos
											</SelectItem>
										</SelectContent>
									</Select>
									{selectedNode.data.escopo === "grupo" ||
									selectedNode.data.escopo === "ambos" ? (
										<p className="text-xs text-amber-600 dark:text-amber-500">
											Em grupo, tudo o que o robô disser aparece para todos os
											participantes. A conexão também precisa estar com "grupos
											viram atendimento" ligado.
										</p>
									) : (
										<p className="text-xs text-muted-foreground">
											O robô não fala em grupos.
										</p>
									)}
								</div>

								{(selectedNode.data.escopo === "grupo" ||
									selectedNode.data.escopo === "ambos") && (
									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerPrivada">
												Responder no privado
											</Label>
											<p className="text-xs text-muted-foreground">
												Veio de grupo, o robô chama quem escreveu na conversa
												individual em vez de falar no grupo.
											</p>
										</div>
										<Switch
											id="triggerPrivada"
											checked={Boolean(selectedNode.data.respostaPrivada)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ respostaPrivada: v })
											}
										/>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "trigger" &&
							selectedNode.data.triggerType === "keyword" && (
								<>
									<div className="space-y-1.5">
										<Label htmlFor="triggerKeyword">Palavras-chave</Label>
										<div className="flex gap-2">
											<Input
												id="triggerKeyword"
												value={novaPalavra}
												placeholder="Ex.: orçamento"
												onChange={(e) => setNovaPalavra(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														e.preventDefault();
														adicionarPalavra();
													}
												}}
											/>
											<Button
												type="button"
												variant="secondary"
												onClick={adicionarPalavra}
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
										{palavrasDoNo.length > 0 && (
											<div className="flex flex-wrap gap-1.5 pt-1">
												{palavrasDoNo.map((palavra) => (
													<Badge
														key={palavra}
														variant="secondary"
														className="gap-1"
													>
														{palavra}
														<button
															type="button"
															aria-label={`Remover ${palavra}`}
															onClick={() => removerPalavra(palavra)}
															className="text-muted-foreground hover:text-foreground"
														>
															<Trash className="h-3 w-3" />
														</button>
													</Badge>
												))}
											</div>
										)}
									</div>

									<div className="space-y-1.5">
										<Label>Como comparar</Label>
										<Select
											value={selectedNode.data.matchMode || "contains"}
											onValueChange={(v) =>
												updateSelectedNodeData({ matchMode: v })
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="contains">
													Contém a palavra
												</SelectItem>
												<SelectItem value="startsWith">
													Começa com a palavra
												</SelectItem>
												<SelectItem value="wholeWord">
													Palavra inteira
												</SelectItem>
												<SelectItem value="exact">
													Mensagem exatamente igual
												</SelectItem>
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Acento é ignorado sempre: "orcamento" encontra
											"orçamento".
										</p>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerCase">
												Diferenciar maiúsculas
											</Label>
											<p className="text-xs text-muted-foreground">
												Com isto ligado, "Orçamento" e "orçamento" são
												palavras diferentes.
											</p>
										</div>
										<Switch
											id="triggerCase"
											checked={Boolean(selectedNode.data.caseSensitive)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ caseSensitive: v })
											}
										/>
									</div>

									<div className="space-y-1.5">
										<Label>Esperar antes de responder (segundos)</Label>
										<div className="flex items-center gap-2">
											<Input
												type="number"
												min={0}
												max={300}
												aria-label="Espera mínima em segundos"
												value={selectedNode.data.delayMin ?? 0}
												onChange={(e) =>
													updateSelectedNodeData({
														delayMin: Number(e.target.value),
													})
												}
											/>
											<span className="text-sm text-muted-foreground">a</span>
											<Input
												type="number"
												min={0}
												max={300}
												aria-label="Espera máxima em segundos"
												value={selectedNode.data.delayMax ?? 0}
												onChange={(e) =>
													updateSelectedNodeData({
														delayMax: Number(e.target.value),
													})
												}
											/>
										</div>
										<p className="text-xs text-muted-foreground">
											Sorteia um tempo entre os dois. Zero nos dois responde na
											hora. Máximo de 300 segundos.
										</p>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerAtendida">
												Não responder se já tem atendente
											</Label>
											<p className="text-xs text-muted-foreground">
												Quando alguém da equipe já assumiu a conversa, o robô
												fica quieto em vez de falar por cima.
											</p>
										</div>
										<Switch
											id="triggerAtendida"
											checked={Boolean(selectedNode.data.naoSeAtendida)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ naoSeAtendida: v })
											}
										/>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerMidia">
												Só quando vier arquivo
											</Label>
											<p className="text-xs text-muted-foreground">
												Responde apenas se a mensagem trouxe foto, áudio,
												vídeo ou documento.
											</p>
										</div>
										<Switch
											id="triggerMidia"
											checked={Boolean(selectedNode.data.somenteComMidia)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ somenteComMidia: v })
											}
										/>
									</div>

									<div className="space-y-1.5">
										<Label>Em quais dias o robô responde</Label>
										<div className="flex gap-1">
											{[
												{ v: 0, l: "D", n: "domingo" },
												{ v: 1, l: "S", n: "segunda" },
												{ v: 2, l: "T", n: "terça" },
												{ v: 3, l: "Q", n: "quarta" },
												{ v: 4, l: "Q", n: "quinta" },
												{ v: 5, l: "S", n: "sexta" },
												{ v: 6, l: "S", n: "sábado" },
											].map((dia) => {
												const atuais = selectedNode.data.diasDaSemana || [];
												const marcado = atuais.includes(dia.v);

												return (
													<button
														key={dia.v}
														type="button"
														aria-label={dia.n}
														aria-pressed={marcado}
														onClick={() =>
															updateSelectedNodeData({
																diasDaSemana: marcado
																	? atuais.filter((d) => d !== dia.v)
																	: [...atuais, dia.v].sort((a, b) => a - b),
															})
														}
														className={cn(
															"h-8 w-8 rounded-full border text-xs font-medium transition-colors",
															marcado
																? "border-primary bg-primary text-primary-foreground"
																: "border-input hover:bg-accent"
														)}
													>
														{dia.l}
													</button>
												);
											})}
										</div>
										<div className="flex items-center gap-2 pt-1">
											<Input
												type="time"
												aria-label="A partir de"
												value={selectedNode.data.horaInicio || ""}
												onChange={(e) =>
													updateSelectedNodeData({
														horaInicio: e.target.value || undefined,
													})
												}
											/>
											<span className="text-sm text-muted-foreground">às</span>
											<Input
												type="time"
												aria-label="Até"
												value={selectedNode.data.horaFim || ""}
												onChange={(e) =>
													updateSelectedNodeData({
														horaFim: e.target.value || undefined,
													})
												}
											/>
										</div>
										<p className="text-xs text-muted-foreground">
											Em branco, responde sempre. Fora da faixa o robô
											simplesmente não fala — diferente do aviso de fora de
											expediente, que responde avisando.
										</p>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="triggerMax">
											No máximo quantas vezes por atendimento
										</Label>
										<Input
											id="triggerMax"
											type="number"
											min={0}
											value={selectedNode.data.maxPorAtendimento ?? 0}
											onChange={(e) =>
												updateSelectedNodeData({
													maxPorAtendimento: Number(e.target.value),
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Zero é sem limite. Com palavra comum, sem teto o fluxo
											recomeça a cada mensagem do cliente.
										</p>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerQuote">Responder citando</Label>
											<p className="text-xs text-muted-foreground">
												A primeira mensagem do fluxo sai citando o que o
												cliente escreveu.
											</p>
										</div>
										<Switch
											id="triggerQuote"
											checked={Boolean(selectedNode.data.quoteReply)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ quoteReply: v })
											}
										/>
									</div>
								</>
							)}

						{selectedNode.type === "sendMessage" && (
							<>
								<div className="space-y-1.5">
									<Label>Conexão</Label>
									<Select
										value={
											selectedNode.data.whatsappId
												? String(selectedNode.data.whatsappId)
												: undefined
										}
										onValueChange={(v) =>
											updateSelectedNodeData({ whatsappId: v })
										}
									>
										<SelectTrigger>
											<SelectValue placeholder="Selecione a conexão" />
										</SelectTrigger>
										<SelectContent>
											{whatsApps.map((w) => (
												<SelectItem key={w.id} value={String(w.id)}>
													{w.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeNumber">Número</Label>
									<Input
										id="nodeNumber"
										value={selectedNode.data.number || ""}
										onChange={(e) =>
											updateSelectedNodeData({ number: e.target.value })
										}
										placeholder="5511999999999"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeBody">Mensagem</Label>
									<Textarea
										id="nodeBody"
										rows={4}
										value={selectedNode.data.body || ""}
										onChange={(e) =>
											updateSelectedNodeData({ body: e.target.value })
										}
										placeholder="Olá {{name}}!"
									/>
									<p className="text-xs text-muted-foreground">
										Use {"{{name}}"} para inserir o nome vindo do gatilho.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "delay" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodeSeconds">Segundos</Label>
								<Input
									id="nodeSeconds"
									type="number"
									min={0}
									max={300}
									value={selectedNode.data.seconds ?? 0}
									onChange={(e) =>
										updateSelectedNodeData({ seconds: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">Máximo de 300s.</p>
							</div>
						)}

						{selectedNode.type === "condition" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="nodeField">Campo</Label>
									<Input
										id="nodeField"
										value={selectedNode.data.field || ""}
										onChange={(e) =>
											updateSelectedNodeData({ field: e.target.value })
										}
										placeholder="ex.: message.body"
									/>
								</div>
								<div className="space-y-1.5">
									<Label>Operador</Label>
									<Select
										value={selectedNode.data.operator || "equals"}
										onValueChange={(v) => updateSelectedNodeData({ operator: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="equals">É igual a</SelectItem>
											<SelectItem value="contains">Contém</SelectItem>
											<SelectItem value="exists">Existe</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{selectedNode.data.operator !== "exists" && (
									<div className="space-y-1.5">
										<Label htmlFor="nodeValue">Valor</Label>
										<Input
											id="nodeValue"
											value={selectedNode.data.value || ""}
											onChange={(e) =>
												updateSelectedNodeData({ value: e.target.value })
											}
										/>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "addTag" && (
							<div className="space-y-1.5">
								<Label>O que fazer</Label>
								<Select
									value={selectedNode.data.acao || "adicionar"}
									onValueChange={(v) =>
										updateSelectedNodeData({
											acao: v,
											// Remover uma etiqueta que ainda não existe não faz
											// sentido: a opção de criar sai junto com a ação.
											...(v === "remover" ? { criarSeNaoExistir: false } : {}),
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="adicionar">Aplicar etiqueta</SelectItem>
										<SelectItem value="remover">Retirar etiqueta</SelectItem>
									</SelectContent>
								</Select>

								<Label className="pt-2">Etiqueta</Label>
								<Select
									value={String(selectedNode.data.tagId || "")}
									onValueChange={(v) => {
										const alvo = etiquetas.find((t) => String(t.id) === v);
										updateSelectedNodeData({
											tagId: v,
											// O nome fica junto para o nó no canvas mostrar algo
											// legível sem depender de outra consulta.
											tagName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha a etiqueta" />
									</SelectTrigger>
									<SelectContent>
										{etiquetas.map((t) => (
											<SelectItem key={t.id} value={String(t.id)}>
												{t.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{selectedNode.data.acao !== "remover" && (
									<div className="space-y-1.5 rounded-lg border p-3">
										<div className="flex items-center justify-between gap-3">
											<div className="space-y-0.5">
												<Label htmlFor="criarEtiqueta">
													Criar se não existir
												</Label>
												<p className="text-xs text-muted-foreground">
													Evita sair daqui só para cadastrar a etiqueta — e
													funciona mesmo para quem não é administrador.
												</p>
											</div>
											<Switch
												id="criarEtiqueta"
												checked={Boolean(selectedNode.data.criarSeNaoExistir)}
												onCheckedChange={(v) =>
													updateSelectedNodeData({ criarSeNaoExistir: v })
												}
											/>
										</div>

										{selectedNode.data.criarSeNaoExistir && (
											<>
												<Input
													value={selectedNode.data.tagName || ""}
													placeholder="Nome da etiqueta"
													onChange={(e) =>
														updateSelectedNodeData({
															tagName: e.target.value,
															// Nome digitado manda: o id de uma etiqueta
															// escolhida antes apontaria para outra coisa.
															tagId: "",
														})
													}
												/>
												<p className="text-xs text-muted-foreground">
													A cor sai do nome, então a mesma etiqueta fica sempre
													da mesma cor.
												</p>
											</>
										)}
									</div>
								)}

								<p className="text-xs text-muted-foreground">
									Só funciona em fluxo disparado por um atendimento.
								</p>
							</div>
						)}

						{selectedNode.type === "transferQueue" && (
							<div className="space-y-1.5">
								<Label>Fila</Label>
								<Select
									value={String(selectedNode.data.queueId || "")}
									onValueChange={(v) => {
										const alvo = filas.find((f) => String(f.id) === v);
										updateSelectedNodeData({
											queueId: v,
											queueName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha a fila" />
									</SelectTrigger>
									<SelectContent>
										{filas.map((f) => (
											<SelectItem key={f.id} value={String(f.id)}>
												{f.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									O atendimento volta para "aguardando" na nova fila.
								</p>
							</div>
						)}

						{selectedNode.type === "note" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodeNote">Texto da nota</Label>
								<Textarea
									id="nodeNote"
									rows={4}
									value={selectedNode.data.note || ""}
									onChange={(e) =>
										updateSelectedNodeData({ note: e.target.value })
									}
									placeholder="Cliente respondeu pela automação."
								/>
								<p className="text-xs text-muted-foreground">
									Fica visível só para a equipe, nunca para o cliente.
								</p>
							</div>
						)}

						{selectedNode.type === "ai" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodePrompt">Instrução para a IA</Label>
								<Textarea
									id="nodePrompt"
									rows={5}
									value={selectedNode.data.prompt || ""}
									onChange={(e) =>
										updateSelectedNodeData({ prompt: e.target.value })
									}
									placeholder="Resuma em uma frase o que o cliente pediu."
								/>
								<p className="text-xs text-muted-foreground">
									A resposta fica disponível como {"{{ai}}"} nos nós seguintes.
								</p>
							</div>
						)}

						{selectedNode.type === "content" && (
							<div className="space-y-2">
								<Label>Mensagens, na ordem</Label>
								{(selectedNode.data.items || []).map((item, i) => (
									<div key={i} className="space-y-1.5 rounded-lg border p-2">
										<div className="flex items-center gap-1.5">
											<Select
												value={item.kind}
												onValueChange={(v) => {
													const itens = [...selectedNode.data.items];
													itens[i] = { ...itens[i], kind: v };
													updateSelectedNodeData({ items: itens });
												}}
											>
												<SelectTrigger className="h-8 flex-1 text-xs">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="text">Texto</SelectItem>
													<SelectItem value="image">Imagem</SelectItem>
													<SelectItem value="audio">Áudio</SelectItem>
													<SelectItem value="video">Vídeo</SelectItem>
													<SelectItem value="document">Documento</SelectItem>
													<SelectItem value="delay">Espera</SelectItem>
												</SelectContent>
											</Select>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
												aria-label={`Remover item ${i + 1}`}
												onClick={() =>
													updateSelectedNodeData({
														items: selectedNode.data.items.filter(
															(_, idx) => idx !== i
														),
													})
												}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>

										{item.kind === "delay" ? (
											<Input
												type="number"
												min={0}
												className="h-8 text-xs"
												aria-label={`Segundos do item ${i + 1}`}
												value={item.seconds ?? 2}
												onChange={(e) => {
													const itens = [...selectedNode.data.items];
													itens[i] = {
														...itens[i],
														seconds: Number(e.target.value),
													};
													updateSelectedNodeData({ items: itens });
												}}
											/>
										) : item.kind === "text" ? (
											<Textarea
												rows={2}
												className="text-xs"
												aria-label={`Texto do item ${i + 1}`}
												placeholder="Olá {{name}}!"
												value={item.value || ""}
												onChange={(e) => {
													const itens = [...selectedNode.data.items];
													itens[i] = { ...itens[i], value: e.target.value };
													updateSelectedNodeData({ items: itens });
												}}
											/>
										) : (
											<>
												<div className="flex gap-1.5">
													<Input
														className="h-8 text-xs"
														aria-label={`Arquivo do item ${i + 1}`}
														placeholder="Escolha um arquivo ou cole um endereço"
														value={item.value || ""}
														onChange={(e) => {
															const itens = [...selectedNode.data.items];
															itens[i] = { ...itens[i], value: e.target.value };
															updateSelectedNodeData({ items: itens });
														}}
													/>
													<Button
														type="button"
														variant="outline"
														size="icon"
														className="h-8 w-8 shrink-0"
														disabled={enviandoArquivo}
														aria-label={`Enviar arquivo do item ${i + 1}`}
														onClick={() => {
															itemDoUpload.current = i;
															arquivoItemRef.current?.click();
														}}
													>
														{enviandoArquivo ? (
															<Loader2 className="h-3.5 w-3.5 animate-spin" />
														) : (
															<Upload className="h-3.5 w-3.5" />
														)}
													</Button>
												</div>
												<Input
													className="h-8 text-xs"
													aria-label={`Legenda do item ${i + 1}`}
													placeholder="Legenda (opcional)"
													value={item.caption || ""}
													onChange={(e) => {
														const itens = [...selectedNode.data.items];
														itens[i] = { ...itens[i], caption: e.target.value };
														updateSelectedNodeData({ items: itens });
													}}
												/>
											</>
										)}
									</div>
								))}

								{/* Um input só para a lista inteira: qual item recebe o
								    arquivo fica em itemDoUpload. */}
								<input
									ref={arquivoItemRef}
									type="file"
									className="hidden"
									onChange={(e) => {
										const alvo = itemDoUpload.current;
										enviarArquivo(e.target.files?.[0], (nome, tipo) => {
											const itens = [...selectedNode.data.items];
											itens[alvo] = {
												...itens[alvo],
												value: nome,
												kind: tipo || itens[alvo].kind,
											};
											updateSelectedNodeData({ items: itens });
										});
										e.target.value = "";
									}}
								/>

								<Button
									variant="outline"
									size="sm"
									className="w-full"
									onClick={() =>
										updateSelectedNodeData({
											items: [
												...(selectedNode.data.items || []),
												{ kind: "text", value: "" },
											],
										})
									}
								>
									<Plus className="h-4 w-4" />
									Adicionar mensagem
								</Button>
								<p className="text-xs text-muted-foreground">
									Saem uma após a outra, nesta ordem.
								</p>
							</div>
						)}

						{selectedNode.type === "integration" && (
							<>
								<div className="space-y-1.5">
									<Label>Serviço</Label>
									<Select
										value={selectedNode.data.service || "http"}
										onValueChange={(v) => updateSelectedNodeData({ service: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="http">Requisição HTTP</SelectItem>
											<SelectItem value="sheets">Google Sheets</SelectItem>
											<SelectItem value="asaas">Asaas (cobrança)</SelectItem>
										</SelectContent>
									</Select>
								</div>

								{(selectedNode.data.service || "http") === "http" && (
									<>
										<div className="space-y-1.5">
											<Label>Método</Label>
											<Select
												value={selectedNode.data.method || "GET"}
												onValueChange={(v) => updateSelectedNodeData({ method: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="GET">GET</SelectItem>
													<SelectItem value="POST">POST</SelectItem>
													<SelectItem value="PUT">PUT</SelectItem>
													<SelectItem value="PATCH">PATCH</SelectItem>
													<SelectItem value="DELETE">DELETE</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="intUrl">URL</Label>
											<Input
												id="intUrl"
												value={selectedNode.data.url || ""}
												onChange={(e) => updateSelectedNodeData({ url: e.target.value })}
												placeholder="https://api.exemplo.com/clientes"
											/>
										</div>

										<div className="space-y-2">
											<Label>Cabeçalhos</Label>
											{(selectedNode.data.headers || []).map((h, i) => (
												<div key={i} className="flex items-center gap-1.5">
													<Input
														className="h-8 text-xs"
														aria-label={`Nome do cabeçalho ${i + 1}`}
														placeholder="Authorization"
														value={h.key || ""}
														onChange={(e) => {
															const hs = [...selectedNode.data.headers];
															hs[i] = { ...hs[i], key: e.target.value };
															updateSelectedNodeData({ headers: hs });
														}}
													/>
													<Input
														className="h-8 text-xs"
														aria-label={`Valor do cabeçalho ${i + 1}`}
														placeholder="Bearer ..."
														value={h.value || ""}
														onChange={(e) => {
															const hs = [...selectedNode.data.headers];
															hs[i] = { ...hs[i], value: e.target.value };
															updateSelectedNodeData({ headers: hs });
														}}
													/>
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
														aria-label={`Remover cabeçalho ${i + 1}`}
														onClick={() =>
															updateSelectedNodeData({
																headers: selectedNode.data.headers.filter(
																	(_, idx) => idx !== i
																),
															})
														}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											))}
											<Button
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													updateSelectedNodeData({
														headers: [
															...(selectedNode.data.headers || []),
															{ key: "", value: "" },
														],
													})
												}
											>
												<Plus className="h-4 w-4" />
												Adicionar cabeçalho
											</Button>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="intBody">Corpo</Label>
											<Textarea
												id="intBody"
												rows={4}
												value={selectedNode.data.body || ""}
												onChange={(e) => updateSelectedNodeData({ body: e.target.value })}
												placeholder='{"nome": "{{name}}"}'
											/>
											<p className="text-xs text-muted-foreground">
												A resposta fica em {"{{http.data}}"} e {"{{http.status}}"}.
											</p>
										</div>
									</>
								)}

								{selectedNode.data.service === "sheets" && (
									<>
										<div className="space-y-1.5">
											<Label>O que fazer</Label>
											<Select
												value={selectedNode.data.operation || "append"}
												onValueChange={(v) => updateSelectedNodeData({ operation: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="append">Gravar uma linha</SelectItem>
													<SelectItem value="read">Ler um intervalo</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="sheetId">ID da planilha</Label>
											<Input
												id="sheetId"
												value={selectedNode.data.spreadsheetId || ""}
												onChange={(e) =>
													updateSelectedNodeData({ spreadsheetId: e.target.value })
												}
												placeholder="1BxiMVs0XRA5nFMdKvBd..."
											/>
											<p className="text-xs text-muted-foreground">
												É o trecho da URL entre /d/ e /edit.
											</p>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="sheetRange">Intervalo</Label>
											<Input
												id="sheetRange"
												value={selectedNode.data.range || ""}
												onChange={(e) => updateSelectedNodeData({ range: e.target.value })}
												placeholder="Página1!A:D"
											/>
										</div>
										{selectedNode.data.operation === "append" && (
											<div className="space-y-1.5">
												<Label htmlFor="sheetValues">Valores da linha</Label>
												<Input
													id="sheetValues"
													value={selectedNode.data.values || ""}
													onChange={(e) =>
														updateSelectedNodeData({ values: e.target.value })
													}
													placeholder="{{name}};{{number}};{{email}}"
												/>
												<p className="text-xs text-muted-foreground">
													Uma coluna por valor, separados por ponto e vírgula.
												</p>
											</div>
										)}
										<div className="space-y-1.5">
											<Label htmlFor="sheetToken">Token de acesso</Label>
											<Input
												id="sheetToken"
												type="password"
												value={selectedNode.data.token || ""}
												onChange={(e) => updateSelectedNodeData({ token: e.target.value })}
											/>
											<p className="text-xs text-muted-foreground">
												Token OAuth do Google com permissão na planilha.
											</p>
										</div>
									</>
								)}

								{selectedNode.data.service === "asaas" && (
									<>
										<div className="space-y-1.5">
											<Label>O que fazer</Label>
											<Select
												value={selectedNode.data.operation || "create"}
												onValueChange={(v) => updateSelectedNodeData({ operation: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="create">Criar cobrança</SelectItem>
													<SelectItem value="status">Consultar cobrança</SelectItem>
												</SelectContent>
											</Select>
										</div>

										{selectedNode.data.operation === "status" ? (
											<div className="space-y-1.5">
												<Label htmlFor="chargeId">ID da cobrança</Label>
												<Input
													id="chargeId"
													value={selectedNode.data.chargeId || ""}
													onChange={(e) =>
														updateSelectedNodeData({ chargeId: e.target.value })
													}
													placeholder="{{asaas.id}}"
												/>
											</div>
										) : (
											<>
												<div className="space-y-1.5">
													<Label htmlFor="chargeValue">
															Valor ({simboloMoeda(moedaPlataforma())})
														</Label>
													<Input
														id="chargeValue"
														value={selectedNode.data.value || ""}
														onChange={(e) =>
															updateSelectedNodeData({ value: e.target.value })
														}
														placeholder="99.90"
													/>
												</div>
												{/* O ExecuteFlowService lê data.dueDate e o editor
												    nunca gravava: toda cobrança saía com o padrão de
												    3 dias, sem jeito de mudar. */}
												<div className="space-y-1.5">
													<Label htmlFor="chargeDueDate">Vence em</Label>
													<Input
														id="chargeDueDate"
														type="date"
														value={selectedNode.data.dueDate || ""}
														onChange={(e) =>
															updateSelectedNodeData({ dueDate: e.target.value })
														}
													/>
													<p className="text-xs text-muted-foreground">
														Em branco, vence em 3 dias. Cobrança que vence no
														mesmo dia chega junto com o susto.
													</p>
												</div>
												<div className="space-y-1.5">
													<Label>Forma de pagamento</Label>
													<Select
														value={selectedNode.data.billingType || "PIX"}
														onValueChange={(v) =>
															updateSelectedNodeData({ billingType: v })
														}
													>
														<SelectTrigger>
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="PIX">PIX</SelectItem>
															<SelectItem value="BOLETO">Boleto</SelectItem>
															<SelectItem value="CREDIT_CARD">Cartão</SelectItem>
														</SelectContent>
													</Select>
												</div>
												<div className="space-y-1.5">
													<Label htmlFor="chargeDesc">Descrição</Label>
													<Input
														id="chargeDesc"
														value={selectedNode.data.description || ""}
														onChange={(e) =>
															updateSelectedNodeData({ description: e.target.value })
														}
													/>
												</div>
											</>
										)}
										<p className="text-xs text-muted-foreground">
											A cobrança sai em {"{{asaas.link}}"}, e {"{{asaas.pago}}"} diz
											se já foi paga. Usa a chave do Asaas das configurações.
										</p>
									</>
								)}
							</>
						)}

						{["menu", "buttons", "list"].includes(selectedNode.type) && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="menuMessage">Pergunta</Label>
									<Textarea
										id="menuMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
										placeholder="Como podemos ajudar?"
									/>
									<p className="text-xs text-muted-foreground">
										As opções abaixo são listadas automaticamente depois dela.
									</p>
								</div>

								<div className="space-y-2">
									<Label>Opções</Label>
									{(selectedNode.data.options || []).map((opcao, i) => (
										<div key={i} className="space-y-1.5">
											<div className="flex items-center gap-1.5">
												<Input
													className="w-14 shrink-0 text-center"
													value={opcao.number}
													aria-label={`Número da opção ${i + 1}`}
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], number: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Input
													value={opcao.label}
													aria-label={`Texto da opção ${i + 1}`}
													placeholder="Falar com vendas"
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], label: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
													aria-label={`Remover opção ${i + 1}`}
													onClick={() => {
														// Remover a opção deixa a ligação dela apontando
														// para o vazio; some com ela junto.
														const alvo = `opt-${opcao.number}`;
														setEdges((eds) =>
															eds.filter(
																(e) =>
																	!(
																		e.source === selectedNode.id &&
																		e.sourceHandle === alvo
																	)
															)
														);
														updateSelectedNodeData({
															options: selectedNode.data.options.filter(
																(_, idx) => idx !== i
															),
														});
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
											{/* A lista mostra uma segunda linha por item; os botões não. */}
											{selectedNode.type === "list" && (
												<Input
													className="ml-[3.875rem] w-[calc(100%-5.875rem)]"
													value={opcao.description || ""}
													aria-label={`Descrição da opção ${i + 1}`}
													placeholder="Descrição (opcional)"
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], description: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
											)}
										</div>
									))}
									<Button
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => {
											const opcoes = selectedNode.data.options || [];
											updateSelectedNodeData({
												options: [
													...opcoes,
													{ number: String(opcoes.length + 1), label: "" },
												],
											});
										}}
									>
										<Plus className="h-4 w-4" />
										Adicionar opção
									</Button>
								</div>

								{selectedNode.type !== "menu" && (
									<>
										{selectedNode.type === "list" && (
											<>
												<div className="space-y-1.5">
													<Label htmlFor="listTitle">Título da lista</Label>
													<Input
														id="listTitle"
														value={selectedNode.data.listTitle || ""}
														placeholder="Nossos serviços"
														onChange={(e) =>
															updateSelectedNodeData({ listTitle: e.target.value })
														}
													/>
												</div>
												<div className="space-y-1.5">
													<Label htmlFor="listButton">
														Texto do botão que abre a lista
													</Label>
													<Input
														id="listButton"
														value={selectedNode.data.buttonText || ""}
														placeholder="Ver opções"
														onChange={(e) =>
															updateSelectedNodeData({ buttonText: e.target.value })
														}
													/>
												</div>
											</>
										)}

										<div className="space-y-1.5">
											<Label htmlFor="menuFooter">Rodapé</Label>
											<Input
												id="menuFooter"
												value={selectedNode.data.footer || ""}
												placeholder="Opcional, sai em letra menor"
												onChange={(e) =>
													updateSelectedNodeData({ footer: e.target.value })
												}
											/>
										</div>

										<p className="text-xs text-muted-foreground">
											O WhatsApp aceita{" "}
											{selectedNode.type === "list" ? "10 itens" : "3 botões"}. Se o
											aparelho de quem recebe não desenhar o menu, a mesma pergunta
											chega como texto numerado — digitar o número continua
											funcionando.
										</p>
									</>
								)}

								<div className="space-y-1.5">
									<Label htmlFor="menuInvalid">Resposta a escolha inválida</Label>
									<Input
										id="menuInvalid"
										value={selectedNode.data.invalidMessage || ""}
										onChange={(e) =>
											updateSelectedNodeData({ invalidMessage: e.target.value })
										}
									/>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="menuAttempts">Tentativas antes de desistir</Label>
									<Input
										id="menuAttempts"
										type="number"
										min={1}
										value={selectedNode.data.maxAttempts ?? 3}
										onChange={(e) =>
											updateSelectedNodeData({
												maxAttempts: Number(e.target.value),
											})
										}
									/>
									<p className="text-xs text-muted-foreground">
										Passando disso o fluxo segue pela saída “desistiu”.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "end" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="endMessage">Despedida (opcional)</Label>
									<Textarea
										id="endMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										placeholder="Obrigado! Se precisar, é só chamar."
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
									/>
								</div>

								<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
									<div className="space-y-0.5">
										<Label htmlFor="endClose">Encerrar o atendimento</Label>
										<p className="text-xs text-muted-foreground">
											Marca como resolvido em vez de deixá-lo aberto para a equipe.
										</p>
									</div>
									<Switch
										id="endClose"
										checked={Boolean(selectedNode.data.closeTicket)}
										onCheckedChange={(v) =>
											updateSelectedNodeData({ closeTicket: v })
										}
									/>
								</div>

								<p className="text-xs text-muted-foreground">
									Depois daqui o robô se cala neste atendimento: nem este fluxo nem
									a saudação recomeçam a cada mensagem do cliente. Ele volta a valer
									quando o atendimento for encerrado.
								</p>
							</>
						)}

						{selectedNode.type === "question" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="questionMessage">Pergunta</Label>
									<Textarea
										id="questionMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
										placeholder="Qual o seu e-mail?"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="questionVar">Guardar a resposta em</Label>
									<Input
										id="questionVar"
										value={selectedNode.data.variable || ""}
										onChange={(e) =>
											updateSelectedNodeData({
												// Só letras, números e _: o nome vira uma chave usada
												// como {{email}} nos nós seguintes.
												variable: e.target.value.replace(/[^\w]/g, ""),
											})
										}
										placeholder="email"
									/>
									<p className="text-xs text-muted-foreground">
										Use {"{{"}
										{selectedNode.data.variable || "nome"}
										{"}}"} nos nós seguintes.
									</p>
								</div>

								{/* Sem formato, "num sei" entrava como e-mail e o resto
								    do fluxo seguia com lixo. */}
								<div className="space-y-1.5">
									<Label>Formato esperado</Label>
									<Select
										value={selectedNode.data.answerType || "texto"}
										onValueChange={(v) =>
											updateSelectedNodeData({ answerType: v })
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="texto">Qualquer texto</SelectItem>
											<SelectItem value="numero">Número</SelectItem>
											<SelectItem value="email">E-mail</SelectItem>
											<SelectItem value="telefone">Telefone</SelectItem>
											<SelectItem value="data">Data (dia/mês/ano)</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-muted-foreground">
										Fora do formato, o robô avisa e pergunta de novo em vez de
										seguir com uma resposta que não serve.
									</p>
								</div>

								{(selectedNode.data.answerType || "texto") !== "texto" && (
									<div className="space-y-1.5">
										<Label htmlFor="questionInvalid">
											Aviso quando não servir
										</Label>
										<Input
											id="questionInvalid"
											value={selectedNode.data.invalidMessage || ""}
											onChange={(e) =>
												updateSelectedNodeData({
													invalidMessage: e.target.value,
												})
											}
											placeholder="Esse e-mail não parece certo. Pode conferir?"
										/>
										<p className="text-xs text-muted-foreground">
											Em branco, o robô usa um aviso padrão.
										</p>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "setVariable" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="varName">Nome da variável</Label>
									<Input
										id="varName"
										value={selectedNode.data.variable || ""}
										onChange={(e) =>
											updateSelectedNodeData({
												variable: e.target.value.replace(/[^\w]/g, ""),
											})
										}
										placeholder="plano"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="varValue">Valor</Label>
									<Input
										id="varValue"
										value={selectedNode.data.value || ""}
										onChange={(e) =>
											updateSelectedNodeData({ value: e.target.value })
										}
										placeholder="Premium"
									/>
									<p className="text-xs text-muted-foreground">
										Pode misturar com o que já existe: {"{{nome}}"} vira o que
										foi guardado antes. Depois use {"{{"}
										{selectedNode.data.variable || "plano"}
										{"}}"} nos nós seguintes.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "jump" && (
							<div className="space-y-1.5">
								<Label>Ir para qual bloco</Label>
								<Select
									value={selectedNode.data.targetNodeId || ""}
									onValueChange={(v) => {
										const alvo = nodes.find((n) => n.id === v);
										updateSelectedNodeData({
											targetNodeId: v,
											targetLabel: alvo ? NODE_LABELS[alvo.type] : "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha o bloco" />
									</SelectTrigger>
									<SelectContent>
										{nodes
											.filter((n) => n.id !== selectedNode.id)
											.map((n) => (
												<SelectItem key={n.id} value={n.id}>
													{NODE_LABELS[n.type] || n.type}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Serve para repetir um menu depois de uma resposta errada, sem
									desenhar tudo de novo. O fluxo para sozinho depois de 50
									passos, então não trava em laço.
								</p>
							</div>
						)}

						{selectedNode.type === "media" && (
							<>
								<div className="space-y-1.5">
									<Label>Tipo</Label>
									<Select
										value={selectedNode.data.mediaType || "image"}
										onValueChange={(v) => updateSelectedNodeData({ mediaType: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="image">Imagem</SelectItem>
											<SelectItem value="video">Vídeo</SelectItem>
											<SelectItem value="audio">Áudio</SelectItem>
											<SelectItem value="document">Documento</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="mediaUrl">Arquivo</Label>
									<div className="flex gap-2">
										<Input
											id="mediaUrl"
											value={selectedNode.data.url || ""}
											onChange={(e) =>
												updateSelectedNodeData({ url: e.target.value })
											}
											placeholder="Escolha um arquivo ou cole um endereço"
										/>
										<Button
											type="button"
											variant="outline"
											size="icon"
											className="shrink-0"
											disabled={enviandoArquivo}
											aria-label="Enviar arquivo do computador"
											onClick={() => arquivoRef.current?.click()}
										>
											{enviandoArquivo ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Upload className="h-4 w-4" />
											)}
										</Button>
									</div>
									<input
										ref={arquivoRef}
										type="file"
										className="hidden"
										onChange={(e) => {
											enviarArquivo(e.target.files?.[0], (nome, tipo) =>
												updateSelectedNodeData({
													url: nome,
													mediaType: tipo || selectedNode.data.mediaType,
												})
											);
											e.target.value = "";
										}}
									/>
									<p className="text-xs text-muted-foreground">
										Pode subir do computador ou colar o endereço de um arquivo
										que já está na internet.
									</p>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="mediaCaption">Legenda</Label>
									<Input
										id="mediaCaption"
										value={selectedNode.data.caption || ""}
										onChange={(e) =>
											updateSelectedNodeData({ caption: e.target.value })
										}
									/>
								</div>
							</>
						)}

						{selectedNode.type === "randomizer" && (
							<div className="space-y-1.5">
								<Label htmlFor="randomPercent">Chance do caminho A (%)</Label>
								<Input
									id="randomPercent"
									type="number"
									min={0}
									max={100}
									value={selectedNode.data.percent ?? 50}
									onChange={(e) =>
										updateSelectedNodeData({ percent: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">
									O resto vai pelo caminho B.
								</p>
							</div>
						)}

						{selectedNode.type === "assignUser" && (
							<div className="space-y-1.5">
								<Label>Atendente</Label>
								<Select
									value={
										selectedNode.data.userId
											? String(selectedNode.data.userId)
											: undefined
									}
									onValueChange={(v) => {
										const alvo = atendentes.find((u) => String(u.id) === v);
										updateSelectedNodeData({
											userId: v,
											userName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha o atendente" />
									</SelectTrigger>
									<SelectContent>
										{atendentes.map((u) => (
											<SelectItem key={u.id} value={String(u.id)}>
												{u.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									O atendimento passa para “atendendo” com essa pessoa.
								</p>
							</div>
						)}

						{selectedNode.type === "ticketStatus" && (
							<div className="space-y-1.5">
								<Label>Situação</Label>
								<Select
									value={selectedNode.data.status || "pending"}
									onValueChange={(v) => updateSelectedNodeData({ status: v })}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="pending">Aguardando</SelectItem>
										<SelectItem value="open">Atendendo</SelectItem>
										<SelectItem value="closed">Resolvido</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}

						{selectedNode.type === "httpRequest" && (
							<>
								<div className="space-y-1.5">
									<Label>Método</Label>
									<Select
										value={selectedNode.data.method || "GET"}
										onValueChange={(v) => updateSelectedNodeData({ method: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="GET">GET</SelectItem>
											<SelectItem value="POST">POST</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeUrl">URL</Label>
									<Input
										id="nodeUrl"
										value={selectedNode.data.url || ""}
										onChange={(e) =>
											updateSelectedNodeData({ url: e.target.value })
										}
										placeholder="https://api.exemplo.com/hook"
									/>
								</div>
								{selectedNode.data.method === "POST" && (
									<div className="space-y-1.5">
										<Label htmlFor="nodeHttpBody">Corpo (JSON)</Label>
										<Textarea
											id="nodeHttpBody"
											rows={4}
											value={selectedNode.data.body || ""}
											onChange={(e) =>
												updateSelectedNodeData({ body: e.target.value })
											}
										/>
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>

			{/* Dialog de teste */}
			<Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Executar teste</DialogTitle>
					</DialogHeader>
					<div className="space-y-1.5">
						<Label htmlFor="testInput">Payload de entrada (JSON)</Label>
						<Textarea
							id="testInput"
							rows={6}
							value={testInput}
							onChange={(e) => setTestInput(e.target.value)}
							className="font-mono text-xs"
						/>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => setTestDialogOpen(false)}
						>
							Cancelar
						</Button>
						<Button type="button" onClick={handleRunTest}>
							<Play className="h-4 w-4" />
							Executar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Painel de execuções */}
			<Sheet open={executionsOpen} onOpenChange={setExecutionsOpen}>
				<SheetContent className="w-full sm:max-w-lg">
					<SheetHeader>
						<SheetTitle className="flex items-center gap-2">
							Execuções
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={fetchExecutions}
							>
								<RefreshCw
									className={`h-3.5 w-3.5 ${
										loadingExecutions ? "animate-spin" : ""
									}`}
								/>
							</Button>
						</SheetTitle>
					</SheetHeader>
					<div className="mt-4 space-y-3 overflow-y-auto pb-6">
						{executions.length === 0 && !loadingExecutions && (
							<p className="text-sm text-muted-foreground">
								Nenhuma execução registrada ainda.
							</p>
						)}
						{executions.map((execution) => (
							<div key={execution.id} className="rounded-lg border bg-card p-3">
								<div className="flex items-center justify-between gap-2">
									<div className="flex items-center gap-2">
										<Badge variant={statusVariant[execution.status] || "secondary"}>
											{statusLabel[execution.status] || execution.status}
										</Badge>
										<span className="text-xs text-muted-foreground">
											#{execution.id}
										</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{execution.createdAt
											? format(parseISO(execution.createdAt), "dd/MM HH:mm:ss")
											: ""}
									</span>
								</div>
								{(execution.log || []).length > 0 && (
									<ul className="mt-2 space-y-1 border-t pt-2">
										{execution.log.map((entry, index) => (
											<li
												key={`${execution.id}-${index}`}
												className="flex items-start gap-2 text-xs"
											>
												<span
													className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
														entry.status === "failed"
															? "bg-destructive"
															: entry.status === "skipped"
															? "bg-muted-foreground"
															: "bg-emerald-500"
													}`}
												/>
												<span className="text-muted-foreground">
													<span className="font-medium text-foreground">
														{NODE_LABELS[entry.type] || entry.type}
													</span>{" "}
													— {entry.message}
												</span>
											</li>
										))}
									</ul>
								)}
							</div>
						))}
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
};

export default FlowBuilder;
